import type {
  MobileFrontEndBubbleMessageView,
  MobileUiConfigArtifact,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { ARTIFACT_ID_FIELD } from './constants';
import { isGraphQLName, toGraphQLLiteral } from './gql-literal';
import { threadKeyFields } from './message-mapping';
import { buildFieldSelection } from './selection-set';

/** Union of every message field the bubble view + detail popover need to select. */
export function messageFieldSelection(
  view: MobileFrontEndBubbleMessageView,
  detailFields: string[],
): string {
  const fields = new Set<string>([ARTIFACT_ID_FIELD, view.body_field, view.timestamp_field]);
  if (view.sender_name_field) fields.add(view.sender_name_field);
  if (view.sender_id_field) fields.add(view.sender_id_field);
  if (view.direction?.owner_field) fields.add(view.direction.owner_field);
  if (view.direction?.field) fields.add(view.direction.field);
  if (view.thread.mode === 'by_field') {
    for (const field of threadKeyFields(view)) fields.add(field);
  } else if (view.thread.parent_link_field) {
    fields.add(view.thread.parent_link_field);
  }
  for (const field of detailFields) fields.add(field);
  for (const source of view.attachments ?? []) {
    fields.add(`${source.items_field}.${source.media_field}`);
    if (source.mime_field) fields.add(`${source.items_field}.${source.mime_field}`);
    if (source.filename_field) fields.add(`${source.items_field}.${source.filename_field}`);
  }
  return buildFieldSelection(fields);
}

/** Build a message list query filtered by a thread field (``where {field: {equals}}``). */
export function buildThreadMessagesQuery(args: {
  rootQuery: string;
  mobileAcquisitionId: number;
  offset: number;
  limit: number;
  whereField: string;
  whereValue: string | number;
  selection: string;
}): string | null {
  if (!isGraphQLName(args.rootQuery) || !isGraphQLName(args.whereField)) return null;
  const where = { [args.whereField]: { equals: args.whereValue } };
  const argParts = [
    `mobileAcquisitionId: ${args.mobileAcquisitionId}`,
    `offset: ${args.offset}`,
    `limit: ${args.limit}`,
    `where: ${toGraphQLLiteral(where)}`,
  ];
  return `query ThreadMessages {
  ${args.rootQuery}(${argParts.join(', ')}) {
    total
    offset
    limit
    items {
        ${args.selection}
    }
  }
}`;
}

/** Build a query fetching a window of messages (used to group ``by_field`` threads client-side). */
export function buildThreadWindowQuery(args: {
  rootQuery: string;
  mobileAcquisitionId: number;
  offset: number;
  limit: number;
  selection: string;
}): string | null {
  if (!isGraphQLName(args.rootQuery)) return null;
  const argParts = [
    `mobileAcquisitionId: ${args.mobileAcquisitionId}`,
    `offset: ${args.offset}`,
    `limit: ${args.limit}`,
  ];
  return `query ThreadWindow {
  ${args.rootQuery}(${argParts.join(', ')}) {
    total
    offset
    limit
    items {
        ${args.selection}
    }
  }
}`;
}

/** Build an exact message count per thread using the message type's groupBy query. */
export function buildThreadCountsQuery(args: {
  rootQuery: string;
  mobileAcquisitionId: number;
  groups: Array<{ field: string; enumValue: string }>;
  limit: number;
}): string | null {
  if (!isGraphQLName(args.rootQuery) || args.groups.length === 0) return null;
  if (
    args.groups.some(
      ({ field, enumValue }) => !isGraphQLName(field) || !isGraphQLName(enumValue),
    )
  ) {
    return null;
  }
  const fields = [...new Set(args.groups.map(({ field }) => field))];
  const enumValues = [...new Set(args.groups.map(({ enumValue }) => enumValue))];
  return `query ThreadCounts {
  ${args.rootQuery}GroupBy(mobileAcquisitionId: ${args.mobileAcquisitionId}, by: [${enumValues.join(', ')}], orderByCount: DESC, limit: ${args.limit}) {
    key {
      ${fields.join('\n      ')}
    }
    count
  }
}`;
}

/**
 * List thread (parent) artifacts for ``by_parent`` threading.
 *
 * Intentionally identical to {@link buildThreadWindowQuery} — a parent thread
 * list is just a windowed list of the parent artifact type. Kept as a named
 * alias so ``by_parent`` call sites read clearly.
 */
export const buildParentThreadListQuery = buildThreadWindowQuery;

/** Resolve the plural root query for the message artifact itself. */
export function messageRootQuery(artifact: MobileUiConfigArtifact | undefined): string | null {
  const name = artifact?.front_end?.graphql_root_query;
  return name && isGraphQLName(name) ? name : null;
}
